import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, CircleCheck, Sparkles, Square, X } from "lucide-react";
import {
  MAX_GROOMING_BATCH_ITEMS,
  type GroomingBatchItemStatus,
  type GroomingBatchItemView,
  type GroomingQueueEntry,
  type GroomingRunnerEvent,
} from "@ttc-spectree/toolbox-contract";
import { applyRunnerEventToCache, isBatchStateEvent } from "@/components/grooming/batch-state";
import { QueueElapsed } from "@/components/grooming/queue/queue-entry-row";
import { RUN_PHASE_LABELS } from "@/components/grooming/run-activity";
import { SessionPicker } from "@/components/grooming/session-picker";
import type { BatchRun } from "@/components/grooming/use-batch-run";
import { Button } from "@/components/ui/button";
import { refreshCachedBacklog, useBacklogBriefs } from "@/hooks/queries/use-brief-staleness";
import { groomingKeys, useMoveAgendaItem, useNextAgenda, useRemoveFromAgenda } from "@/hooks/queries/use-grooming";
import type { GroomingBatch, GroomingMeetingPattern } from "@/lib/api/grooming";
import { dateParts } from "@/lib/grooming/agenda-run";
import { agendaErrorMessage, groomingApiErrorCode } from "@/lib/grooming/error-messages";
import { formatLocalClockParts } from "@/lib/grooming/local-time";
import { carryOverDate, formatMeetingDate, isPastMeeting, meetingMonthDay } from "@/lib/grooming/meeting-dates";
import { GROOMING_QUEUE_PATH } from "@/lib/grooming/paths";
import { LEFT_OUT_TEXT, batchItemBriefState, briefStateTag } from "@/lib/grooming/plain-words";
import { useRunnerBridge, useRunnerEvents } from "@/lib/grooming/runner-bridge";
import { showGroomingToast } from "@/lib/grooming/toasts";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * Session after Prepare (PKG-PM-C, ER-1638, feature PI-SPEC-SW-1492; UX-PM-20;
 * Claude Design "PM Package C Mockups", frame C4). The run's progress lives
 * on the Session screen itself, so the Project Manager never hunts for it on
 * another page:
 *
 * - left: "Preparing 6 briefs · 2 of 6 done", how long is left and where it
 *   runs, a meter, and one row per item with its step and timer (running),
 *   "Up next" (waiting) or its brief state in the shared words (UX-PM-09);
 * - right: "Briefs prepared" ("Preparing briefs" while it runs; was "This
 *   agenda is set") with Stop preparing (`prepare-sessions`), and
 *   "The meeting after this one" with **Start the next meeting's agenda**
 *   (`manage-agenda`), which replaces the build step's old note about
 *   ticking an item. It starts the agenda for the meeting after
 *   the one this run was prepared for (01-roles.md §9.6), the same rule as
 *   the Agenda view's Carry over; the session read then shows that draft.
 *
 * E323 (ER-1710, feature PI-SPEC-SW-1739): a prepared agenda stays editable
 * until its meeting has passed. `manage-agenda` gets Remove and Move on every
 * row; an item added after the run finished shows "Needs a brief", and
 * "Prepare N new items" (`prepare-sessions`, a grooming-4 app) starts the
 * completed run again for just those. Once the meeting date is before today
 * in the pattern's time zone the agenda is a record: no edit controls, and a
 * plain note says so.
 *
 * The header ("Preparing since …", Open the briefs) is the Session page's.
 * PKG-PM-B's pause banners, Fix it and the awake line stay on the Queue page;
 * this screen links there.
 */

/** The first grooming contract whose app restarts a completed agenda for its new items (E323). */
export const RESTART_RUN_CONTRACT = 4;

/** Item statuses that mean the run is done with the item. */
const FINISHED: ReadonlySet<GroomingBatchItemStatus> = new Set(["briefed", "not-ready", "failed", "skipped", "current"]);

function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}

/** The run's counts: items, done (finished one way or another) and with a brief. */
export function preparedCounts(run: Pick<GroomingBatch, "items">): { total: number; done: number; briefed: number } {
  return {
    total: run.items.length,
    done: run.items.filter((item) => FINISHED.has(item.status)).length,
    briefed: run.items.filter((item) => item.status === "briefed" || item.status === "current").length,
  };
}

/**
 * The progress heading: "Preparing 6 briefs · 2 of 6 done" while it runs (or
 * waits in the queue), "6 briefs ready" when finished, "Preparing stopped ·
 * 2 of 6 done" when stopped. Pure; exported for tests.
 */
export function preparedHeading(run: Pick<GroomingBatch, "status" | "items">): string {
  const { total, done, briefed } = preparedCounts(run);
  const of = `${String(done)} of ${String(total)} done`;
  if (run.status === "completed") {
    // E323: items added after the run finished wait for "Prepare N new items".
    const added = newItemCount(run);
    const need = added === 0 ? "" : ` · ${String(added)} ${added === 1 ? "needs a brief" : "need a brief"}`;
    return `${plural(briefed, "brief", "briefs")} ready${need}`;
  }
  if (run.status === "stopped") return `Preparing stopped · ${of}`;
  return `Preparing ${plural(total, "brief", "briefs")} · ${of}`;
}

/**
 * How many items a finished (`completed`) run holds that still wait for a
 * brief: added after it finished (E323). 0 for any other status. Pure.
 */
export function newItemCount(run: Pick<GroomingBatch, "status" | "items">): number {
  return run.status === "completed" ? run.items.filter((item) => item.status === "queued").length : 0;
}

/** The right card's title: "Preparing briefs" while it runs, "Preparing stopped", else "Briefs prepared". Pure. */
export function preparedCardTitle(status: GroomingBatch["status"]): string {
  if (status === "stopped") return "Preparing stopped";
  if (status === "completed" || status === "canceled") return "Briefs prepared";
  return "Preparing briefs";
}

/**
 * The right card's sentence (E323): what was prepared and, for a person who
 * may change the agenda (`canEdit`: `manage-agenda` and the meeting not
 * passed), that it can still change until the meeting. Never tells anyone to
 * do what the API would refuse. Pure; exported for tests.
 */
export function preparedCardText(
  run: Pick<GroomingBatch, "status" | "items">,
  weekday: string,
  canEdit: boolean
): string {
  const { total, briefed } = preparedCounts(run);
  const items = plural(total, "item", "items");
  if (run.status === "completed" || run.status === "canceled") {
    const until = canEdit ? " You can still add, remove or move items until the meeting." : "";
    return `The ${items} above were prepared for ${weekday}.${until}`;
  }
  if (run.status === "stopped") {
    const until = canEdit ? " You can still add, remove or move items until the meeting." : "";
    return `Preparing stopped with ${plural(briefed, "brief", "briefs")} for ${weekday}. The ${items} above stay on this agenda.${until}`;
  }
  const meanwhile = canEdit ? " You can add, remove or move items while this runs." : "";
  return `The ${items} above are being prepared for ${weekday}.${meanwhile}`;
}

/** The note on an agenda whose meeting has passed (E323). */
export const AGENDA_LOCKED_TEXT = "This meeting has passed, so its agenda is now a record.";

/** "Fri 2:10 PM", "2:10 PM" today, "yesterday 4:40 PM"; null for a missing time. */
function sinceWords(iso: string | null | undefined): string | null {
  const parts = typeof iso === "string" ? formatLocalClockParts(iso) : null;
  if (!parts) return null;
  return parts.day ? `${parts.day} ${parts.time}` : parts.time;
}

/**
 * What the Session header says instead of the prepare-by hint once the agenda
 * is prepared (frame C4): "Preparing since Fri 2:10 PM", "Briefs ready since
 * …", "Preparing stopped at …". Pure; exported for tests.
 */
export function preparedStatus(
  run: Pick<GroomingBatch, "status" | "startedAt" | "finishedAt" | "createdAt">
): { text: string; tone: "running" | "ready" | "stopped" } {
  if (run.status === "completed") {
    const since = sinceWords(run.finishedAt);
    return { text: since ? `Briefs ready since ${since}` : "Briefs ready", tone: "ready" };
  }
  if (run.status === "stopped") {
    const at = sinceWords(run.finishedAt);
    return { text: at ? `Preparing stopped at ${at}` : "Preparing stopped", tone: "stopped" };
  }
  const since = sinceWords(run.startedAt);
  return { text: since ? `Preparing since ${since}` : "Preparing now", tone: "running" };
}

/** One row's step: its brief state, its live step and timer, or why it has none. */
export type PreparedStep =
  | { kind: "done"; text: string }
  | { kind: "running"; text: string; startedAt: string | null }
  | { kind: "waiting"; text: string }
  | { kind: "problem"; text: string };

/**
 * An item's step on the prepared view. A briefed item says its brief state in
 * the shared words (with Claude's size while not posted, stage-1 trim §7.2);
 * a running one the app's phase ("Reading the code") and its start time for
 * the timer. On a finished run (`finished`), a waiting item was added after
 * it finished and says "Needs a brief" (E323). Pure; exported for tests.
 */
export function preparedStep(
  item: GroomingBatchItemView,
  stopped: boolean,
  latest: Parameters<typeof batchItemBriefState>[1],
  entry: GroomingQueueEntry | null,
  finished = false
): PreparedStep {
  switch (item.status) {
    case "briefed":
    case "current": {
      const state = batchItemBriefState(item, latest);
      const words = briefStateTag(state).text;
      const points =
        state === "not-posted" && item.status === "briefed" && item.brief ? ` · ${String(item.brief.suggestedPoints)} pts` : "";
      return { kind: "done", text: `${words}${points}` };
    }
    case "running": {
      const text = entry?.phase ? RUN_PHASE_LABELS[entry.phase] : entry ? "Starting…" : "Analysing";
      return { kind: "running", text, startedAt: entry?.startedAt ?? item.startedAt ?? null };
    }
    case "queued":
      if (entry?.state === "running") {
        return { kind: "running", text: entry.phase ? RUN_PHASE_LABELS[entry.phase] : "Starting…", startedAt: entry.startedAt };
      }
      if (finished && !entry) return { kind: "waiting", text: "Needs a brief" };
      return { kind: "waiting", text: stopped && !entry ? "Not started" : "Up next" };
    case "not-ready":
      return { kind: "problem", text: "Needs information" };
    case "failed":
      return { kind: "problem", text: "Couldn't finish" };
    case "skipped":
      return { kind: "waiting", text: LEFT_OUT_TEXT };
  }
}

/** "About 12 minutes left" from the typical time an item takes and how many run at once; null without a typical time. */
export function minutesLeftText(remaining: number, typicalItemSeconds: number | null | undefined, atOnce: number): string | null {
  if (remaining <= 0 || !typicalItemSeconds || !(typicalItemSeconds > 0)) return null;
  const minutes = Math.max(1, Math.ceil((remaining * typicalItemSeconds) / Math.max(1, atOnce) / 60));
  return `About ${plural(minutes, "minute", "minutes")} left`;
}

/** `2026-10-26` → "Monday"; "the meeting" without a date. */
function weekdayOf(date: string | null): string {
  return date ? dateParts(date).toLocaleDateString("en-US", { weekday: "long" }) : "the meeting";
}

/**
 * Follow the run live in the app: each runner event for it is folded into the
 * cached batch and the batch read again (as the run step does), a new brief
 * marks the cached backlog out of date, and a start, stop or finish re-reads
 * the project's session and batches. A browser polls the batch instead.
 */
function usePreparedRunEvents(project: string, batchId: string): void {
  const { runner } = useRunnerBridge();
  const queryClient = useQueryClient();
  const onEvent = useCallback(
    (event: GroomingRunnerEvent) => {
      if (!isBatchStateEvent(event, batchId)) return;
      const applied = applyRunnerEventToCache(queryClient, batchId, event);
      if (event.type === "item-finished") refreshCachedBacklog(queryClient, project);
      if (event.type === "item-started" || event.type === "batch-stopped" || event.type === "batch-finished") {
        void applied.then(() => {
          void queryClient.invalidateQueries({ queryKey: groomingKeys.batches(project) });
          void queryClient.invalidateQueries({ queryKey: groomingKeys.session(project) });
        });
      }
    },
    [queryClient, batchId, project]
  );
  useRunnerEvents(runner, onEvent);
}

/** This item's entry in the run queue (running or waiting), if any. */
function entryFor(batchRun: BatchRun, item: GroomingBatchItemView): GroomingQueueEntry | null {
  const all = [...batchRun.queueEntries.running, ...batchRun.queueEntries.upNext];
  return all.find((entry) => entry.batchItemId === item.id) ?? all.find((entry) => entry.itemId === item.itemId) ?? null;
}

function StepCell({ step }: { step: PreparedStep }) {
  if (step.kind === "running") {
    return (
      <span className="flex items-center gap-1.5 text-xs text-[#6CC4FF]" data-testid="prepared-step" data-kind="running">
        <span className="h-2.5 w-2.5 flex-none animate-spin rounded-full border-2 border-[#23A4FF] border-r-transparent" aria-hidden="true" />
        <span>{step.text}</span>
        <span aria-hidden="true">·</span>
        <QueueElapsed startedAt={step.startedAt} />
      </span>
    );
  }
  if (step.kind === "done") {
    return (
      <span className="flex items-center gap-1.5 text-xs font-semibold text-green-400" data-testid="prepared-step" data-kind="done">
        <CircleCheck className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        {step.text}
      </span>
    );
  }
  return (
    <span
      className={cn("text-xs", step.kind === "problem" ? "text-amber-400" : "text-muted-foreground")}
      data-testid="prepared-step"
      data-kind={step.kind}
    >
      {step.text}
    </span>
  );
}

export interface SessionPreparedProps {
  project: string;
  /** The prepared agenda run (running, stopped or finished; a just-started draft counts as running). */
  run: GroomingBatch;
  /** The meeting the run was prepared for (`YYYY-MM-DD`), null when unknown. */
  sessionDate: string | null;
  meetingPattern: GroomingMeetingPattern | null;
  /** Median recent brief run in seconds, for "About N minutes left"; null without history. */
  typicalItemSeconds: number | null;
  /** Start and stop for this run, from the Session page (`useBatchRun`). */
  batchRun: BatchRun;
  /** `prepare-sessions` on the project: Stop preparing and Resume. */
  canPrepare: boolean;
  /**
   * `manage-agenda` on the project: Start the next meeting's agenda, and
   * Remove and Move on each row until the meeting has passed (E323).
   */
  canManageAgenda: boolean;
  /**
   * The next meeting's agenda when one is started, as a draft or a run
   * prepared early (`prepared`; PKG-PM-C Mac walk, 2026-10-23): its date,
   * item count and the path that opens it. The card then offers Open instead
   * of Start, so no second agenda is made, and this run stays on the page.
   */
  nextAgenda?: { date: string | null; items: number; href: string; prepared: boolean } | null;
  /** After Start the next meeting's agenda succeeds (the Session page opens that agenda). */
  onNextAgendaStarted?: () => void;
  /** False on the next meeting's own page (`?agenda=next`): no "The meeting after this one" card there. */
  showMeetingAfter?: boolean;
}

/** Frame C4: the run's progress and what can happen next, on the Session screen. */
export function SessionPrepared({
  project,
  run,
  sessionDate,
  meetingPattern,
  typicalItemSeconds,
  batchRun,
  canPrepare,
  canManageAgenda,
  nextAgenda = null,
  onNextAgendaStarted,
  showMeetingAfter = true,
}: SessionPreparedProps) {
  const { runner } = useRunnerBridge();
  const bridge = useToolboxBridge();
  usePreparedRunEvents(project, run.id);
  const latestOf = useBacklogBriefs(project);
  const startAgenda = useNextAgenda(project);
  const remove = useRemoveFromAgenda(project);
  const move = useMoveAgendaItem(project);
  const [nextError, setNextError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const { total, done } = preparedCounts(run);
  const stopped = run.status === "stopped";
  const finished = run.status === "completed" || run.status === "canceled";
  const preparing = !finished && !stopped;
  const left = run.items.filter((item) => item.status === "queued").length;
  const atOnce = batchRun.queueState?.concurrency ?? 1;
  const minutes = preparing ? minutesLeftText(total - done, typicalItemSeconds ?? run.typicalItemSeconds ?? null, atOnce) : null;
  const fill = total ? `${String(Math.round((done / total) * 100))}%` : "0%";
  const weekday = weekdayOf(sessionDate);
  const target = carryOverDate(run, meetingPattern);
  const canRun = canPrepare && runner !== null;
  // E323: the agenda changes until its meeting has passed (the API's GROOMING_AGENDA_LOCKED after that).
  // Only the run's own date can pass while it is shown: an undated run here was prepared for the coming meeting.
  const locked = isPastMeeting(run.sessionDate ?? null, meetingPattern);
  const editable = canManageAgenda && !locked && run.status !== "canceled";
  const added = newItemCount(run);
  // A completed run starts again for its new items only in a grooming-4 app (feature-detected; the Toolbox update pairs it).
  const canRestart = (bridge?.app.contracts.grooming ?? 0) >= RESTART_RUN_CONTRACT;
  const editBusy = remove.isPending || move.isPending;

  const editFailed = (cause: unknown, fallback: string) => {
    setPendingId(null);
    setEditError(
      agendaErrorMessage(cause) ??
        (groomingApiErrorCode(cause) === "GROOMING_BATCH_FULL"
          ? `That meeting's agenda is full (an agenda holds ${String(MAX_GROOMING_BATCH_ITEMS)}).`
          : fallback)
    );
  };

  const removeItem = (itemId: string) => {
    setEditError(null);
    setPendingId(itemId);
    remove.mutate(
      { itemId, batchId: run.id },
      {
        onSuccess: () => {
          setPendingId(null);
          showGroomingToast(sessionDate ? `Removed from the ${meetingMonthDay(sessionDate)} session` : "Removed from the session", "info");
        },
        onError: (cause) => {
          editFailed(cause, "The item could not be removed. Try again in a moment.");
        },
      }
    );
  };

  const moveItem = (itemId: string, toDate: string) => {
    setEditError(null);
    setPendingId(itemId);
    move.mutate(
      { batchId: run.id, itemId, toDate },
      {
        onSuccess: () => {
          setPendingId(null);
          showGroomingToast(`Moved to the ${meetingMonthDay(toDate)} session`);
        },
        onError: (cause) => {
          editFailed(cause, "The item could not be moved. Try again in a moment.");
        },
      }
    );
  };

  const startNext = () => {
    setNextError(null);
    startAgenda.mutate(
      { runId: run.id, itemIds: [] },
      {
        onSuccess: (result) => {
          showGroomingToast(
            result.targetDate ? `Started the agenda for ${formatMeetingDate(result.targetDate)}` : "Started the next meeting's agenda"
          );
          onNextAgendaStarted?.();
        },
        onError: (cause) => {
          const code = groomingApiErrorCode(cause);
          setNextError(
            code === "ADO_PAT_REQUIRED"
              ? "Save your Azure DevOps token in Setup first, then try again."
              : "The next meeting's agenda could not be started. Try again in a moment."
          );
        },
      }
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row" data-testid="session-prepared">
      {/* The run's progress */}
      <section
        className="glass-card flex min-h-[320px] min-w-0 flex-1 flex-col overflow-hidden rounded-[10px]"
        aria-labelledby="prepared-heading"
      >
        <div className="flex items-center gap-3 border-b border-[#1F1F23] px-4 py-3.5">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h2 id="prepared-heading" className="font-display text-sm font-semibold" data-testid="prepared-heading">
              {preparedHeading(run)}
            </h2>
            {preparing && (
              <span className={cn(TOOLBOX_TEXT.meta, "text-muted-foreground")} data-testid="prepared-meta">
                {minutes ? `${minutes} · ` : ""}
                {batchRun.queueMode ? (
                  <>
                    runs in the{" "}
                    <Link to={GROOMING_QUEUE_PATH} className="text-link hover:underline">
                      Queue
                    </Link>
                    , {String(atOnce)} at a time
                  </>
                ) : (
                  "runs in SpecTree Toolbox"
                )}{" "}
                · you can close this screen
              </span>
            )}
          </div>
          <div
            className="h-1.5 w-[140px] flex-none overflow-hidden rounded-full bg-[#27272A]"
            role="progressbar"
            aria-label="Briefs prepared"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={done}
          >
            <div className="h-full rounded-full bg-[#23A4FF] transition-[width] duration-500" style={{ width: fill }} />
          </div>
        </div>
        <ol className="min-h-0 overflow-auto" aria-label="Items being prepared">
          {[...run.items]
            .sort((a, b) => a.position - b.position)
            .map((item, index) => (
              <li
                key={item.id}
                className={cn(
                  "grid h-[44px] items-center gap-3 border-b border-[#141417] px-4",
                  editable
                    ? "grid-cols-[22px_minmax(0,1fr)_minmax(150px,auto)_auto]"
                    : "grid-cols-[22px_minmax(0,1fr)_minmax(150px,auto)]"
                )}
                data-testid="prepared-row"
                data-item-id={item.itemId}
              >
                <span className="flex h-[22px] w-[22px] items-center justify-center rounded-md bg-[#1A1A1E] text-[11px] font-semibold text-muted-foreground">
                  {index + 1}
                </span>
                <span className="min-w-0 truncate text-[13px]" title={`AB#${item.itemId} · ${item.title}`}>
                  {pendingId === item.itemId ? "Saving… " : ""}
                  {item.title}
                </span>
                <span className="justify-self-end">
                  <StepCell
                    step={preparedStep(item, stopped, latestOf(item.itemId), entryFor(batchRun, item), run.status === "completed")}
                  />
                </span>
                {editable && (
                  <span className="flex items-center gap-1" data-testid="prepared-row-actions">
                    <SessionPicker
                      project={project}
                      currentBatchId={run.id}
                      label="Move"
                      testId="prepared-move"
                      disabled={editBusy}
                      onPick={(date) => {
                        moveItem(item.itemId, date);
                      }}
                    />
                    <button
                      type="button"
                      className={cn(
                        "flex h-7 w-7 items-center justify-center rounded hover:bg-[#1A1A1E] hover:text-foreground disabled:opacity-40",
                        TOOLBOX_TEXT.faint
                      )}
                      aria-label={`Remove AB#${item.itemId} from the agenda`}
                      data-testid="prepared-remove"
                      disabled={editBusy}
                      onClick={() => {
                        removeItem(item.itemId);
                      }}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </span>
                )}
              </li>
            ))}
        </ol>
        {editError && (
          <p className="border-t border-[#1F1F23] px-4 py-2 text-[13px] text-destructive" role="alert" data-testid="prepared-edit-error">
            {editError}
          </p>
        )}
      </section>

      {/* What can happen next */}
      <div className="flex w-full flex-none flex-col gap-3 lg:w-[340px]">
        <section className="glass-card flex flex-col gap-2.5 rounded-[10px] px-[18px] py-4" data-testid="agenda-set">
          <h2 className={TOOLBOX_TEXT.eyebrow}>{preparedCardTitle(run.status)}</h2>
          <p className={cn(TOOLBOX_TEXT.content, "leading-relaxed text-muted-foreground")} data-testid="agenda-set-text">
            {preparedCardText(run, weekday, editable)}
          </p>
          {locked && (
            <p className={cn(TOOLBOX_TEXT.content, "leading-relaxed text-foreground")} data-testid="agenda-locked">
              {AGENDA_LOCKED_TEXT}
            </p>
          )}
          {canPrepare && !locked && added > 0 && !batchRun.active &&
            (canRun && canRestart ? (
              <Button
                type="button"
                size="sm"
                className="self-start border-primary bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={batchRun.busy || batchRun.liveRun}
                data-testid="prepare-new-items"
                onClick={() => {
                  void batchRun.start();
                }}
              >
                <Sparkles className="h-3 w-3" aria-hidden="true" />
                Prepare {plural(added, "new item", "new items")}
              </Button>
            ) : (
              <p className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")} data-testid="prepare-new-items-note">
                {runner === null ? "Open SpecTree Toolbox to prepare new items." : "Update SpecTree Toolbox to prepare new items."}
              </p>
            ))}
          {canRun && batchRun.active && !stopped && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              disabled={batchRun.busy}
              onClick={() => {
                void batchRun.stop();
              }}
            >
              <Square className="h-3 w-3" aria-hidden="true" />
              Stop preparing
            </Button>
          )}
          {canRun && stopped && left > 0 && (
            <Button
              type="button"
              size="sm"
              className="self-start border-primary bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={batchRun.busy || batchRun.liveRun}
              onClick={() => {
                void batchRun.start();
              }}
            >
              Resume ({left} left)
            </Button>
          )}
          {batchRun.error && (
            <p className="text-[13px] text-destructive" role="alert">
              {batchRun.error}
            </p>
          )}
        </section>

        {canManageAgenda && showMeetingAfter && (
          <section className="glass-card flex flex-col gap-2.5 rounded-[10px] px-[18px] py-4" data-testid="meeting-after">
            <h2 className={TOOLBOX_TEXT.eyebrow}>The meeting after this one</h2>
            {nextAgenda ? (
              <>
                <p className={cn(TOOLBOX_TEXT.content, "leading-relaxed text-muted-foreground")}>
                  The agenda for{" "}
                  <b className="font-semibold text-foreground" data-testid="meeting-after-date">
                    {nextAgenda.date ? formatMeetingDate(nextAgenda.date) : "the next meeting"}
                  </b>{" "}
                  is {nextAgenda.prepared ? "prepared" : "started"} · {plural(nextAgenda.items, "item", "items")}.{" "}
                  {weekday === "the meeting" ? "This agenda" : `${weekday}'s agenda`} stays as it is.
                </p>
                <Button asChild size="sm" variant="outline" className="self-start">
                  <Link to={nextAgenda.href} data-testid="open-next-agenda">
                    <CalendarPlus aria-hidden="true" />
                    Open the next meeting&apos;s agenda
                  </Link>
                </Button>
              </>
            ) : (
              <>
                <p className={cn(TOOLBOX_TEXT.content, "leading-relaxed text-muted-foreground")}>
                  Something new for the meeting after? Start the agenda for{" "}
                  <b className="font-semibold text-foreground" data-testid="meeting-after-date">
                    {target ? formatMeetingDate(target) : "the next meeting"}
                  </b>{" "}
                  now. {weekday === "the meeting" ? "This agenda" : `${weekday}'s agenda`} stays as it is.
                </p>
                <Button
                  type="button"
                  size="sm"
                  className="self-start border-primary bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={startAgenda.isPending}
                  onClick={startNext}
                >
                  <CalendarPlus aria-hidden="true" />
                  Start the next meeting&apos;s agenda
                </Button>
              </>
            )}
            {nextError && (
              <p className="text-[13px] text-destructive" role="alert">
                {nextError}
              </p>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
